import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  RETURN_PATH_KEY,
  isOfflineSession,
  isSafeReturnPath,
  takeReturnPath,
} from '../../utils/offlineSession';

const LANDING = '/action-plan';

/**
 * Task 6.0ag. What the /login route renders once someone is signed in: where
 * they go next. Navigation only -- whether a kept offline session belongs to
 * the person who signed in is decided in AuthContext, on every sign-in, before
 * `currentUser` is set (ConfirmEmail signs in without ever visiting /login).
 *
 * This is the route's redirect, not code in Login, on purpose: sign-in sets
 * `currentUser`, which swaps Login out for this element, and a navigate()
 * called from Login after `await login()` lost that race (the user always
 * ended on the default page, whatever `from` said).
 *
 * - Offline session still on (so it is this account's): the remembered page,
 *   still offline, with no /token-setup detour (offline mode exists for users
 *   whose Canvas token does not work).
 * - Otherwise: the remembered page, or /token-setup if the account has no
 *   Canvas credentials, as before.
 */
const SignedInRedirect = () => {
  const { hasCanvasCredentials } = useAuth();

  // Decided once, on the first render: consuming the return path changes what
  // this would compute next time, and a changed target would navigate again.
  const [target] = useState(() => {
    // sessionStorage is the only source. ProtectedRoute also passes the page
    // in router state, but that state outlives the storage: when a different
    // account signs in, AuthContext removes the stored path and the state
    // would still name the previous account's page. Validated, so it can
    // neither send the user off-app nor back to /login.
    const stored = sessionStorage.getItem(RETURN_PATH_KEY);
    const remembered = isSafeReturnPath(stored) ? stored : null;

    if (isOfflineSession()) return remembered || LANDING;
    return hasCanvasCredentials() ? remembered || LANDING : '/token-setup';
  });

  // The remembered page is used up once the navigation has been issued.
  useEffect(() => {
    takeReturnPath();
  }, []);

  return <Navigate to={target} replace />;
};

export default SignedInRedirect;
